import { useEffect, useRef, useState } from 'react';

export default function ProcessStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps = [
    {
      number: '01',
      title: 'Contacto Inicial',
      description: 'Nos llamas o envías fotos. Evaluamos la urgencia y te orientamos sobre los primeros pasos.',
      icon: 'ri-phone-line'
    },
    {
      number: '02',
      title: 'Inspección y Presupuesto',
      description: 'Visitamos el lugar (si es posible antes del perito). Documentamos daños y preparamos presupuesto.',
      icon: 'ri-search-eye-line'
    },
    {
      number: '03',
      title: 'Protección y Contención',
      description: 'Sellamos áreas afectadas, protegemos lo salvable y preparamos el espacio para la limpieza.',
      icon: 'ri-shield-line'
    },
    {
      number: '04',
      title: 'Limpieza Técnica',
      description: 'Aspirado HEPA, lavado por material, desodorización y desinfección con equipos profesionales.',
      icon: 'ri-tools-line'
    },
    {
      number: '05',
      title: 'Entrega y Checklist',
      description: 'Revisión final contigo. Espacio listo para reformas con documentación completa.',
      icon: 'ri-checkbox-circle-line'
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const scrollProgress = entry.intersectionRatio;
            const newStep = Math.min(Math.floor(scrollProgress * steps.length), steps.length - 1);
            setActiveStep(newStep);
          }
        });
      },
      { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-bg overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif font-bold text-3xl sm:text-4xl md:text-5xl text-primary mb-6">
            Cómo trabajamos paso a paso
          </h2>
          <p className="text-textLight text-base sm:text-lg max-w-2xl mx-auto">
            Un proceso claro y transparente desde el primer contacto hasta la entrega final.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-300 transform md:-translate-x-1/2"></div>

          {/* Steps */}
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative flex flex-col md:flex-row items-start md:items-center gap-8 transition-opacity duration-500 opacity-100"
              >
                {/* Left Content (odd) / Right Content (even) */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-16' : 'md:order-2 md:pl-16'}`}>
                  <div className="bg-white rounded-lg p-6 shadow-md">
                    <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                      <span className="text-secondary font-serif font-bold text-2xl sm:text-3xl">{step.number}</span>
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-secondary/10 rounded-full ${index % 2 === 0 ? 'md:order-first' : ''}`}>
                        <i className={`${step.icon} text-xl sm:text-2xl text-secondary`}></i>
                      </div>
                    </div>
                    <h3 className="font-sans font-bold text-lg sm:text-xl text-primary mb-2">
                      {step.title}
                    </h3>
                    <p className="text-textLight text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Center Node */}
                <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-secondary rounded-full border-4 border-bg shadow-md flex items-center justify-center">
                  {index <= activeStep && (
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  )}
                </div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden md:block"></div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-textLight mb-6">
            ¿Quieres conocer más detalles sobre cada servicio?
          </p>
          <a
            href="/servicios"
            className="inline-block text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer text-lg"
          >
            Ver descripción completa de servicios →
          </a>
        </div>
      </div>
    </section>
  );
}
